import React from "react";

import type { InventoryScanResult } from "@/api/inventory";
import type { CharOption } from "@/api/order_check";
import { CharacteristicChoice } from "@/lib/scan-ui/characteristic-choice";

/**
 * Вопрос «какая характеристика?» в шторке скана инвентаризации.
 *
 * ⚠️ Правило склада «что не предлагать при скане» — ПОДСКАЗКА, А НЕ ЗАПРЕТ.
 * Пока шторка рисовала только `available_characteristics`, в ветке «осталось
 * 2+» правило превращалось именно в запрет: у кода варианты ОПТ / WB /
 * «никакая», правило убрало ОПТ — а на полке лежит ОПТ, и зафиксировать это
 * нечем. Исправить постфактум тоже нечем: на вопрос отвечал ЧЕЛОВЕК, значит
 * `characteristic_autofilled=false`, и кнопки «изменить» в карточке скана нет.
 * Поэтому отброшенные варианты доступны здесь же — спокойной строкой-
 * раскрытием, чтобы обычный выбор остался коротким.
 *
 * Компонент чистый: шторкой и запросом владеет экран смены (от шторки зависят
 * перехват сканера и аппаратная «назад»).
 */
export function CharacteristicAsk({
  res,
  busy,
  onPick,
}: {
  res: InventoryScanResult;
  /** Ответ уже улетел на сервер — кнопки заблокированы (второй тап = вторая
   *  строка номенклатуры в документе 1С). */
  busy: boolean;
  /** ⚠️ Отдаём ВЫБРАННЫЙ ВАРИАНТ: ссылку `none` разворачивает сервер. */
  onPick: (option: CharOption) => void;
}): React.ReactElement {
  const [showHidden, setShowHidden] = React.useState(false);
  const dropped = res.hidden_characteristics;
  const all = allDropped(res);
  const answer = { charRef: "", charName: "", withoutChar: false };
  return (
    <>
      <h3 className="text-base font-semibold">Какая характеристика?</h3>
      {/* ⚠️ Правило склада могло отбросить ВСЕ варианты — тогда сервер задаёт
          вопрос со всеми и объясняет в `warning`, почему находка странная.
          Этот текст обязан дойти до человека: иначе он молча выберет то, чего
          тут не бывает. Узнаём случай по составу: в этой ветке отброшенные
          лежат и в предложенных, в обычной — списки не пересекаются. */}
      {all ? (
        <p className="mt-1 text-xs text-amber-700">{res.warning || res.message}</p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          У этого штрихкода их несколько — портал не угадывает.
        </p>
      )}
      {/* ⚠️ Ссылку шлём КАК ЕСТЬ: `none` — это ОТВЕТ «никакая», и разворачивает
          его сервер. Пустая строка означала бы «не ответил», и вопрос пошёл бы
          по кругу.
          ⚠️ Пока повтор летит, кнопки заблокированы (`busy`): ответ идёт около
          секунды, и второй тап успевал уйти вторым сканом — в другую кнопку,
          то есть второй строкой той же номенклатуры в документе 1С. Подпись
          «Записываю…» рисует сам компонент. */}
      <div className="mt-3">
        <CharacteristicChoice
          options={res.available_characteristics}
          answer={answer}
          onPick={onPick}
          required
          busy={busy}
        />
      </div>

      {/* Отброшенные правилом — под раскрытием: в обычном случае их не видно
          и выбор короткий, но найденный на складе товар всегда есть чем
          зафиксировать. */}
      {!all && dropped.length > 0 && !showHidden && (
        <button
          type="button"
          className="mt-2 text-left text-xs text-slate-500 underline"
          onClick={() => setShowHidden(true)}
        >
          На этом складе не предлагаются: {dropped.map((c) => c.name).join(", ")} —
          показать
        </button>
      )}
      {!all && dropped.length > 0 && showHidden && (
        <div className="mt-2">
          <p className="text-xs text-amber-700">
            Этих характеристик на складе быть не должно — выберите, если товар
            действительно такой.
          </p>
          <div className="mt-1">
            <CharacteristicChoice
              options={dropped}
              answer={answer}
              onPick={onPick}
              busy={busy}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** Правило склада отбросило ВСЕ варианты кода: сервер задаёт вопрос со
 *  всеми сразу, и отброшенные лежат и в предложенных. В обычной ветке
 *  («осталось 2+») списки не пересекаются. */
export function allDropped(res: InventoryScanResult): boolean {
  return res.hidden_characteristics.some((h) =>
    res.available_characteristics.some((a) => a.ref === h.ref),
  );
}
